import Box from '@mui/material/Box'
import React from 'react'

import { EXCEPTION_MEDIA_KIND, getExceptionMediaSlideKey } from 'utils/exceptionImage'
import { get } from 'utils/lodash'
import { isValidLatitudeRange, isValidLongitudeRange } from 'utils/validations'

import ExceptionCardBottomOverlay from '../ExceptionCardBottomOverlay'
import ExceptionCardMediaSwiperContainer from '../ExceptionCardMediaSwiperContainer'
import ExceptionCardTopOverlay from '../ExceptionCardTopOverlay'
import ExceptionImage from '../ExceptionImage'
import ExceptionMapImage from '../ExceptionMapImage'
import ImagePlaceholder from '../ImagePlaceholder'

import ExceptionVideoSlide from './ExceptionVideoSlide'

import type { ExceptionImageItem, ExceptionShape } from './types'

// Image area: photo gallery (swiper) with a GPS mini-map toggle in the corner, date +
// timestamp overlays burned over a dim gradient, and a NO-IMG placeholder. Toggling the
// GPS overlay swaps the gallery for the satellite map. The orchestrator owns the toggle
// state so a card click can route to the photo gallery vs the map dialog.
type ExceptionCardImageProps = {
  exception: ExceptionShape
  images: ExceptionImageItem[]
  accountId?: string
  workOrderId?: string
  date?: string
  time?: string
  isImageView: boolean
  onToggleView: () => void
  PageProps?: Record<string, unknown>
  ImageSx?: Record<string, unknown>
}

const ExceptionCardTopOverlayUntyped = ExceptionCardTopOverlay as React.ElementType
const ExceptionCardMediaSwiperContainerUntyped = ExceptionCardMediaSwiperContainer as React.ElementType
const ExceptionImageUntyped = ExceptionImage as React.ElementType
const ImagePlaceholderUntyped = ImagePlaceholder as React.ElementType
const ExceptionCardBottomOverlayUntyped = ExceptionCardBottomOverlay as React.ElementType

const ExceptionCardImage: React.FC<ExceptionCardImageProps> = ({
  exception,
  images,
  accountId,
  workOrderId,
  date,
  time,
  isImageView,
  onToggleView,
  PageProps,
  ImageSx,
}) => {
  const id = get(exception, 'id')
  const latitude = get(exception, 'latitude')
  const longitude = get(exception, 'longitude')
  const isValidLatLong = isValidLatitudeRange(latitude) && isValidLongitudeRange(longitude)
  const hasImages = Array.isArray(images) && images.length > 0

  return (
    <Box sx={{ position: 'relative' }}>
      {isValidLatLong && (
        <ExceptionCardTopOverlayUntyped
          isExceptionImageView={isImageView}
          latitude={latitude}
          longitude={longitude}
          onClick={onToggleView}
        />
      )}

      {!isImageView && <ExceptionMapImage height={194} id={id} latitude={latitude} longitude={longitude} />}

      {isImageView && (
        <ExceptionCardMediaSwiperContainerUntyped images={images}>
          {/* Same 194px as photo/PDF slides so every card's media area lines up. */}
          {!hasImages && <ImagePlaceholderUntyped height={194} />}
          {hasImages &&
            images.map(image =>
              get(image, 'kind') === EXCEPTION_MEDIA_KIND.VIDEO ? (
                <ExceptionVideoSlide key={getExceptionMediaSlideKey(image)} height={194} thumbnailUri={image.thumbnailUri} />
              ) : (
                <ExceptionImageUntyped
                  key={getExceptionMediaSlideKey(image)}
                  withSwiperSlide
                  accountId={accountId}
                  exception={exception}
                  ImageSx={ImageSx}
                  PageProps={PageProps}
                  photo={image}
                  size="small"
                  workOrderId={workOrderId}
                />
              )
            )}
        </ExceptionCardMediaSwiperContainerUntyped>
      )}

      {/* Absolute scrim overlay — a sibling of the swiper on purpose: as a swiper child
          it lands in the slide wrapper and pagination counts it as an extra slide. */}
      <ExceptionCardBottomOverlayUntyped date={date} time={time} />
    </Box>
  )
}

export default ExceptionCardImage
